import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Activity } from "lucide-react";
import { format } from "date-fns";

interface Row {
  id: string;
  user_id: string;
  ip_address: string | null;
  user_agent: string | null;
  device: string | null;
  logged_in_at: string;
  full_name?: string;
}

const LoginActivity = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("admin_login_activity" as any)
        .select("*")
        .order("logged_in_at", { ascending: false })
        .limit(200);
      const list = (data ?? []) as any[];
      const userIds = [...new Set(list.map((r) => r.user_id))];
      const { data: profiles } = userIds.length
        ? await supabase.from("profiles").select("id, full_name").in("id", userIds)
        : { data: [] as any[] };
      const nameMap = new Map<string, string>(
        (profiles ?? []).map((p: any) => [p.id, p.full_name ?? ""])
      );
      setRows(list.map((r) => ({ ...r, full_name: nameMap.get(r.user_id) ?? "" })));
      setLoading(false);
    })();
  }, []);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Activity className="h-7 w-7" /> Login Activity
          </h1>
          <p className="text-muted-foreground mt-1">Recent admin sign-ins with device and IP.</p>
        </div>
        <Card>
          <CardHeader><CardTitle>Recent Logins ({rows.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-muted-foreground">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">No login activity yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Browser</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {format(new Date(r.logged_in_at), "MMM d, yyyy HH:mm")}
                        </TableCell>
                        <TableCell className="text-sm">{r.full_name || r.user_id.slice(0, 8)}</TableCell>
                        <TableCell className="text-sm">{r.device || "—"}</TableCell>
                        <TableCell className="text-sm font-mono text-xs">{r.ip_address || "—"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">
                          {r.user_agent || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default LoginActivity;
